import { createHash, randomBytes } from 'node:crypto'
import type { ModuleContext, CollectionEntityDefinition } from '@gzl10/nexus-sdk'
import { generateTokenPair, generateAccessToken, getRefreshTokenExpiration } from './jwt.utils.js'
import type { JwtPayload, TokenPair } from './jwt.utils.js'
import { refreshTokenEntity, authIdentitiesEntity } from './auth.entity.js'
import { personalTokenEntity } from './auth.pat.entity.js'
import { OTPManager } from './otp-manager.js'
import type { AuthConfig, LoginInput, RegisterInput, ForgotPasswordInput, ResetPasswordInput, RequestInfo, UserSession, AuthUserRecord, AuthIdentity, LinkIdentityInput, CreatePersonalTokenInput, PersonalToken, PersonalTokenScope } from './auth.types.js'
import type { UsersService } from '../users/users.types.js'
import type { MailService } from '../mail/mail.service.js'
import type { AuditService } from '../audit/audit.types.js'
import { checkLeakedPassword } from './hibp.js'

// Table names (hardcoded for module isolation - auth depends on users via ctx.services)
const USERS = 'users'
const REFRESH_TOKENS = (refreshTokenEntity as CollectionEntityDefinition).table
const AUTH_IDENTITIES = authIdentitiesEntity.table
const PERSONAL_TOKENS = personalTokenEntity.table

export type AuthService = ReturnType<typeof createAuthService>

export function createAuthService(ctx: ModuleContext, authConfig: AuthConfig) {
  const { errors, abilities, crypto } = ctx.core
  const { generateId } = ctx.core
  const { nowTimestamp } = ctx.db
  const { verifyPassword, DUMMY_HASH } = crypto
const { defineAbilityFor, packRules } = abilities
  const ErrorCodes = errors.codes
  const db = ctx.db.knex

  // Rate-limit and OTP cache (no event invalidation — TTL-based only)
  const authCache = ctx.core.cache.create<number | { code: string; ip: string }>('auth:rate-limit', {
    maxEntries: 1000,
    defaultTTL: 900 // 15 min default
  })

  // PAT validation cache — invalidated when tokens change
  const patValidationCache = ctx.core.cache.create<{ userId: string; scope: PersonalTokenScope; tokenId: string }>('auth:pat', {
    maxEntries: 500,
    defaultTTL: 60,
    invalidateOn: ['db.personal_access_tokens.*']
  })

  // HIBP range cache — long TTL, breach data doesn't change frequently
  const hibpCache = ctx.core.cache.create<string>('auth:hibp-ranges', {
    maxEntries: 500,
    defaultTTL: 86400 // 24h in seconds
  })

  // Obtener servicio de usuarios (type-safe desde ctx)
  const usersService = ctx.services.get<UsersService>('users')
  const { roles: rolesService } = usersService

  // Inicializar OTP Manager (centraliza generación, validación y envío de OTP)
  const mailService = ctx.services.getOptional<MailService>('mail')
  const otpManager = new OTPManager(authCache, mailService, ctx.core.logger)

  // Audit service (core module)
  const auditService = ctx.services.get<AuditService>('audit')

  /** Generate token pair — catches hook errors gracefully */
  async function safeGenerateTokenPair(payload: JwtPayload, user: Record<string, unknown>): Promise<TokenPair> {
    try {
      return await generateTokenPair(payload, authConfig, user)
    } catch (error) {
      ctx.core.logger.warn({ err: error }, '[auth] onTokenCreate hook failed, using base claims')
      return await generateTokenPair(payload, { ...authConfig, onTokenCreate: undefined })
    }
  }

  /** Generate access token — catches hook errors gracefully */
  async function safeGenerateAccessToken(payload: JwtPayload, user: Record<string, unknown>): Promise<string> {
    try {
      return await generateAccessToken(payload, authConfig, user)
    } catch (error) {
      ctx.core.logger.warn({ err: error }, '[auth] onTokenCreate hook failed, using base claims')
      return await generateAccessToken(payload, { ...authConfig, onTokenCreate: undefined })
    }
  }

  /** Get user with roles (multi-role support) */
  async function getUserWithRoles(userId: string) {
    return usersService.findByIdWithRoles(userId)
  }

  /**
   * Checks rate limit and validates OTP if threshold exceeded
   * @returns true if OTP was validated (password check should be skipped)
   * @throws AppError if OTP required but not provided
   * @throws ForbiddenError if OTP invalid
   */
  async function checkRateLimit(
    email: string,
    otp: string | undefined,
    requestInfo?: RequestInfo
  ): Promise<boolean> {
    const attemptKey = `login_attempts:${email}`
    const ipKey = `login_attempts:ip:${requestInfo?.ip ?? 'unknown'}`

    const emailAttempts = (await authCache.get(attemptKey) as number) ?? 0
    const ipAttempts = (await authCache.get(ipKey) as number) ?? 0
    const threshold = authConfig.challengeThreshold

    // If threshold exceeded, require OTP
    if (emailAttempts >= threshold || ipAttempts >= threshold) {
      if (!otp) {
        const { otpSent, expiresIn } = await otpManager.generateAndSend({
          email,
          type: 'login',
          requestInfo
        })
        throw new errors.AppError('OTP required', 403, { otpSent, expiresIn })
      }

      if (!await otpManager.validate(email, otp, 'login')) {
        throw new errors.ForbiddenError('Invalid OTP')
      }

      await otpManager.clear(email, 'login')

      // OTP validated - password check should be skipped
      return true
    }

    // No OTP required - normal password validation
    return false
  }

  /**
   * Authenticates user credentials (timing-safe)
   * @param skipPasswordCheck - If true (OTP was validated), skip password verification
   * @returns Authenticated user record
   * @throws UnauthorizedError if credentials invalid
   */
  async function authenticateUser(
    email: string,
    password: string,
    requestInfo?: RequestInfo,
    skipPasswordCheck = false
  ): Promise<AuthUserRecord> {
    const attemptKey = `login_attempts:${email}`
    const ipKey = `login_attempts:ip:${requestInfo?.ip ?? 'unknown'}`

    const user = await db<AuthUserRecord>(USERS).where({ email }).first()

    if (!user) {
      // Timing-safe: always run bcrypt to prevent user enumeration via response time
      await verifyPassword(password, DUMMY_HASH)
      ctx.events.notify('auth.failed', { email, reason: 'user_not_found' })
      auditService.log({ source: 'core:auth', action: 'login_failed', actorEmail: email, ip: requestInfo?.ip, userAgent: requestInfo?.userAgent, metadata: { reason: 'user_not_found' } })
      throw new errors.UnauthorizedError(ErrorCodes['AUTH_INVALID_CREDENTIALS'], 'Invalid credentials')
    }

    // Block login for non-human accounts (bots and services cannot login via password)
    if (user.type !== 'human') {
      ctx.events.notify('auth.failed', { email, reason: 'non_human_login_attempt' })
      auditService.log({ source: 'core:auth', action: 'login_failed', actorEmail: email, ip: requestInfo?.ip, userAgent: requestInfo?.userAgent, metadata: { reason: 'non_human_login_attempt', userType: user.type } })
      throw new errors.UnauthorizedError(ErrorCodes['AUTH_INVALID_CREDENTIALS'], 'Invalid credentials')
    }

    // If OTP was validated, skip password check
    if (skipPasswordCheck) {
      // Clear attempt counters on success
      await authCache.delete(attemptKey)
      await authCache.delete(ipKey)
      return user
    }

    // Normal password validation (timing-safe)
    const emailAttempts = (await authCache.get(attemptKey) as number) ?? 0
    const ipAttempts = (await authCache.get(ipKey) as number) ?? 0

    // Timing-safe: siempre ejecutar bcrypt aunque usuario no exista
    // Note: password is guaranteed non-null for human users (validated above)
    const hashToCheck = user.password!
    const validPassword = await verifyPassword(password, hashToCheck)

    if (!validPassword) {
      ctx.events.notify('auth.failed', { email, reason: 'invalid_password' })

      // Increment attempt counters
      await authCache.set(attemptKey, emailAttempts + 1, 900) // 15 min TTL
      await authCache.set(ipKey, ipAttempts + 1, 900)

      auditService.log({ source: 'core:auth', action: 'login_failed', actorEmail: email, ip: requestInfo?.ip, userAgent: requestInfo?.userAgent, metadata: { reason: 'invalid_password' } })

      throw new errors.UnauthorizedError(ErrorCodes['AUTH_INVALID_CREDENTIALS'], 'Invalid credentials')
    }

    // Clear attempt counters on success
    await authCache.delete(attemptKey)
    await authCache.delete(ipKey)

    return user
  }

  /**
   * Creates user session with tokens and abilities
   */
  async function createUserSession(
    user: AuthUserRecord,
    requestInfo?: RequestInfo
  ) {
    const roleIds = await usersService.getRoleIds(user.id)
    const roleNames = await usersService.getRoleNames(user.id)

    // Note: email is guaranteed non-null for human users (only humans can login)
    const tokens = await safeGenerateTokenPair({
      userId: user.id,
      email: user.email!,
      roleIds,
      roleNames
    }, user as Record<string, unknown>)

    await db(REFRESH_TOKENS).insert({
      id: generateId(),
      token: tokens.refreshToken,
      user_id: user.id,
      expires_at: getRefreshTokenExpiration(authConfig),
      device_id: requestInfo?.deviceId ?? null,
      device_name: requestInfo?.deviceName ?? null
    })

    const ability = await defineAbilityFor(user, roleNames)
    const userWithRoles = await getUserWithRoles(user.id)

    ctx.events.notify('auth.login', { userId: user.id, email: user.email! })
    auditService.log({ source: 'core:auth', action: 'login', actorId: user.id, actorEmail: user.email ?? undefined, ip: requestInfo?.ip, userAgent: requestInfo?.userAgent })

    return {
      user: userWithRoles,
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      abilities: packRules(ability)
    }
  }

  /** Validate password against HIBP if enabled. Throws ForbiddenError in block mode. */
  async function validatePasswordNotLeaked(password: string): Promise<{ leaked: boolean; warning?: string }> {
    if (!authConfig.hibpCheck) return { leaked: false }

    const count = await checkLeakedPassword(password, {
      cache: hibpCache,
      logger: ctx.core.logger
    })

    if (count === 0) return { leaked: false }

    if (authConfig.hibpMode === 'block') {
      throw new errors.ForbiddenError(
        'PASSWORD_LEAKED',
        'This password has been found in a data breach. Please choose a different password.'
      )
    }

    // warn mode — return warning but allow
    return {
      leaked: true,
      warning: `This password has appeared in ${count.toLocaleString()} data breaches. Consider changing it.`
    }
  }

  return {
    async login(input: LoginInput, requestInfo?: RequestInfo) {
      const otpValidated = await checkRateLimit(input.email, input.otp, requestInfo)
      const user = await authenticateUser(input.email, input.password, requestInfo, otpValidated)
      return createUserSession(user, requestInfo)
    },

    async register(input: RegisterInput, requestInfo?: RequestInfo) {
      // Enforce registration policy (moved from action handler — domain invariant)
      if (authConfig.disableRegistration) {
        throw new errors.ForbiddenError('AUTH_REGISTRATION_DISABLED', 'Registration is disabled')
      }

      // Require OTP for registration (email verification) unless skipped for tests
      if (!input.otp && !authConfig.skipRegisterOtp) {
        // Generate and send OTP if it doesn't exist
        const { otpSent, expiresIn } = await otpManager.generateAndSend({
          email: input.email,
          type: 'register',
          requestInfo
        })

        throw new errors.AppError('OTP required', 403, { otpSent, expiresIn })
      }

      // Validate OTP (skip if configured for testing)
      if (!authConfig.skipRegisterOtp) {
        if (!await otpManager.validate(input.email, input.otp!, 'register')) {
          throw new errors.ForbiddenError('Invalid OTP')
        }
        // OTP valid, clear it
        await otpManager.clear(input.email, 'register')
      }

      // Check leaked password (after OTP validation, before user creation)
      const hibpResult = await validatePasswordNotLeaked(input.password)

      // Buscar rol por defecto (VIEWER)
      const defaultRole = await rolesService.findByName('VIEWER')
      if (!defaultRole) {
        throw new errors.AppError('Default role VIEWER not found. Run migrations.', 500)
      }

      // Crear usuario (usersService hace hash de password y valida email único)
      const user = await usersService.create({
        email: input.email,
        password: input.password,
        name: input.name
      })

      // Assign default role via pivot table
      await usersService.assignRole(user.id, defaultRole.id)

      // Generar tokens (email is guaranteed for registration)
      const tokens = await safeGenerateTokenPair({
        userId: user.id,
        email: user.email!,
        roleIds: [defaultRole.id],
        roleNames: [defaultRole.name]
      }, user as Record<string, unknown>)

      // Guardar refresh token en DB
      await db(REFRESH_TOKENS).insert({
        id: generateId(),
        token: tokens.refreshToken,
        user_id: user.id,
        expires_at: getRefreshTokenExpiration(authConfig),
        device_id: requestInfo?.deviceId ?? null,
        device_name: requestInfo?.deviceName ?? null
      })

      // Build ability from role names (permissions are static in entity definitions)
      const ability = await defineAbilityFor(user, [defaultRole.name])

      // Obtener usuario con roles
      const userWithRoles = await getUserWithRoles(user.id)

      ctx.events.notify('auth.login', { userId: user.id, email: user.email! })

      // Persistir evento de registro
      auditService.log({ source: 'core:auth', action: 'register', actorId: user.id, actorEmail: user.email ?? undefined, ip: requestInfo?.ip, userAgent: requestInfo?.userAgent })

      return {
        user: userWithRoles,
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        abilities: packRules(ability),
        ...(hibpResult.warning && { warning: hibpResult.warning })
      }
    },

    async refresh(refreshToken: string, requestInfo?: RequestInfo) {
      // Validate + delete old token atomically to prevent concurrent reuse.
      // The DELETE inside the transaction ensures a second request with the same
      // token will find it missing and fail immediately.
      const validated = await db.transaction(async (trx) => {
        let query = trx(REFRESH_TOKENS).where({ token: refreshToken })
        const client = db.client.config.client as string
        if (!client.includes('sqlite')) {
          query = query.forUpdate()
        }
        const storedToken = await query.first()

        if (!storedToken) {
          throw new errors.UnauthorizedError('Refresh token inválido')
        }

        if (new Date(storedToken.expires_at) < new Date()) {
          await trx(REFRESH_TOKENS).where({ id: storedToken.id }).delete()
          throw new errors.UnauthorizedError('Refresh token expirado')
        }

        const user = await trx<AuthUserRecord>(USERS).where({ id: storedToken.user_id }).first()
        if (!user) {
          throw new errors.UnauthorizedError('Usuario no encontrado')
        }

        // Delete consumed token inside the same transaction (closes race condition)
        await trx(REFRESH_TOKENS).where({ id: storedToken.id }).delete()

        return { user, storedToken }
      })

      // Roles and token generation outside transaction to avoid SQLite deadlock
      const roleIds = await usersService.getRoleIds(validated.user.id)
      const roleNames = await usersService.getRoleNames(validated.user.id)

      const tokens = await safeGenerateTokenPair({
        userId: validated.user.id,
        email: validated.user.email!,
        roleIds,
        roleNames
      }, validated.user as Record<string, unknown>)

      // Insert new refresh token (no race risk — old token already consumed)
      await db(REFRESH_TOKENS).insert({
        id: generateId(),
        token: tokens.refreshToken,
        user_id: validated.user.id,
        expires_at: getRefreshTokenExpiration(authConfig),
        device_id: validated.storedToken.device_id ?? null,
        device_name: validated.storedToken.device_name ?? null
      })

      const ability = await defineAbilityFor(validated.user, roleNames)

      ctx.events.notify('auth.refresh', { userId: validated.user.id })

      auditService.log({ source: 'core:auth', action: 'refresh', actorId: validated.user.id, ip: requestInfo?.ip, userAgent: requestInfo?.userAgent })

      return {
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        abilities: packRules(ability)
      }
    },

    async logout(refreshToken: string, userId?: string, requestInfo?: RequestInfo) {
      const deleted = await db(REFRESH_TOKENS).where({ token: refreshToken }).delete()
      if (deleted > 0 && userId) {
        ctx.events.notify('auth.logout', { userId })

        auditService.log({ source: 'core:auth', action: 'logout', actorId: userId, ip: requestInfo?.ip, userAgent: requestInfo?.userAgent })
      }
      return deleted > 0
    },

    async logoutAll(userId: string, requestInfo?: RequestInfo) {
      const deleted = await db(REFRESH_TOKENS).where({ user_id: userId }).delete()

      ctx.events.notify('auth.logout', { userId })

      auditService.log({ source: 'core:auth', action: 'logout', actorId: userId, ip: requestInfo?.ip, userAgent: requestInfo?.userAgent, metadata: { allDevices: true, sessionsRevoked: deleted } })

      return deleted
    },

    async me(userId: string) {
      const userWithRoles = await getUserWithRoles(userId)

      if (!userWithRoles) {
        throw new errors.NotFoundError('Usuario')
      }

      // Load role names for ability building
      const roleNames = await usersService.getRoleNames(userId)
      const ability = await defineAbilityFor(userWithRoles, roleNames)

      return {
        user: userWithRoles,
        abilities: packRules(ability)
      }
    },

    async forgotPassword(input: ForgotPasswordInput, requestInfo?: RequestInfo) {
      // Always show success message for security (don't reveal if email exists)
      const user = await db<AuthUserRecord>(USERS).where({ email: input.email }).first()

      if (user) {
        // Generate and send password reset OTP
        await otpManager.generateAndSend({
          email: input.email,
          type: 'reset',
          requestInfo
        })
      }

      // Always return success (security: don't reveal if email exists)
      return { sent: true }
    },

    async resetPassword(input: ResetPasswordInput, requestInfo?: RequestInfo) {
      // Validate OTP
      if (!await otpManager.validate(input.email, input.otp, 'reset')) {
        throw new errors.ForbiddenError('Invalid or expired verification code')
      }

      // Find user
      const user = await db<AuthUserRecord>(USERS).where({ email: input.email }).first()
      if (!user) {
        throw new errors.NotFoundError('User')
      }

      // Check leaked password before allowing reset
      const hibpResult = await validatePasswordNotLeaked(input.newPassword)

      // Hash new password and update
      const hashedPassword = await crypto.hashPassword(input.newPassword)
      await db(USERS).where({ id: user.id }).update({
        password: hashedPassword,
        updated_at: nowTimestamp(db)
      })

      // Clear OTP
      await otpManager.clear(input.email, 'reset')

      // Invalidate all refresh tokens (force re-login on all devices)
      await db(REFRESH_TOKENS).where({ user_id: user.id }).delete()

      auditService.log({ source: 'core:auth', action: 'password_reset', actorId: user.id, actorEmail: user.email ?? undefined, ip: requestInfo?.ip, userAgent: requestInfo?.userAgent })

      return { success: true, ...(hibpResult.warning && { warning: hibpResult.warning }) }
    },

    async cleanupExpiredTokens() {
      const deleted = await db(REFRESH_TOKENS)
        .where('expires_at', '<', nowTimestamp(db))
        .delete()
      return deleted
    },

    async getSessions(userId: string, currentToken?: string): Promise<UserSession[]> {
      const tokens = await db(REFRESH_TOKENS)
        .where({ user_id: userId })
        .where('expires_at', '>', new Date())
        .select('id', 'device_id', 'device_name', 'token', 'created_at', 'expires_at')
        .orderBy('created_at', 'desc')

      return tokens.map(t => ({
        id: t.id,
        device_id: t.device_id ?? null,
        device_name: t.device_name ?? null,
        created_at: t.created_at,
        expires_at: t.expires_at,
        is_current: currentToken ? t.token === currentToken : false
      }))
    },

    async revokeSession(userId: string, sessionId: string, requestInfo?: RequestInfo): Promise<boolean> {
      // Solo permite revocar sesiones del propio usuario
      const deleted = await db(REFRESH_TOKENS)
        .where({ id: sessionId, user_id: userId })
        .delete()

      if (deleted > 0) {
        auditService.log({ source: 'core:auth', action: 'logout', actorId: userId, ip: requestInfo?.ip, userAgent: requestInfo?.userAgent, metadata: { sessionId, revokedByUser: true } })
      }

      return deleted > 0
    },

    async impersonate(adminUserId: string, targetUserId: string, requestInfo?: RequestInfo) {
      // Verify admin has ADMIN/OWNER role
      const adminRoleNames = await usersService.getRoleNames(adminUserId)
      const SUPERUSER_ROLES = ['ADMIN', 'OWNER']
      if (!adminRoleNames.some(r => SUPERUSER_ROLES.includes(r))) {
        throw new errors.ForbiddenError('Only administrators can impersonate users')
      }

      // Cannot impersonate yourself
      if (adminUserId === targetUserId) {
        throw new errors.ForbiddenError('Cannot impersonate yourself')
      }

      // Verify target is not an admin
      const targetRoleNames = await usersService.getRoleNames(targetUserId)
      if (targetRoleNames.some(r => SUPERUSER_ROLES.includes(r))) {
        throw new errors.ForbiddenError('Cannot impersonate administrators')
      }

      // Load target user
      const targetUser = await db<AuthUserRecord>(USERS).where({ id: targetUserId }).first()
      if (!targetUser) {
        throw new errors.NotFoundError('User')
      }

      // Generate access token with impersonation marker (no refresh token)
      const targetRoleIds = await usersService.getRoleIds(targetUserId)
      const accessToken = await safeGenerateAccessToken({
        userId: targetUserId,
        email: targetUser.email!,
        roleIds: targetRoleIds,
        roleNames: targetRoleNames,
        impersonatedBy: adminUserId
      }, targetUser as Record<string, unknown>)

      // Build abilities for target user
      const ability = await defineAbilityFor(targetUser, targetRoleNames)
      const targetWithRoles = await getUserWithRoles(targetUserId)

      auditService.log({ source: 'core:auth', action: 'impersonate_start', actorId: adminUserId, ip: requestInfo?.ip, userAgent: requestInfo?.userAgent, resourceType: 'user', resourceId: targetUserId, metadata: { targetEmail: targetUser.email } })

      return {
        user: targetWithRoles,
        accessToken,
        abilities: packRules(ability),
        impersonation: { isImpersonating: true, originalUserId: adminUserId }
      }
    },

    async stopImpersonate(adminUserId: string, requestInfo?: RequestInfo) {
      // Reload admin data
      const adminUser = await db<AuthUserRecord>(USERS).where({ id: adminUserId }).first()
      if (!adminUser) {
        throw new errors.NotFoundError('Admin user')
      }

      const adminRoleNames = await usersService.getRoleNames(adminUserId)
      const ability = await defineAbilityFor(adminUser, adminRoleNames)
      const adminWithRoles = await getUserWithRoles(adminUserId)

      auditService.log({ source: 'core:auth', action: 'impersonate_stop', actorId: adminUserId, ip: requestInfo?.ip, userAgent: requestInfo?.userAgent })

      return {
        user: adminWithRoles,
        abilities: packRules(ability)
      }
    },

    async resetCache() { await authCache.clear() },

    // === GDPR / Cross-module isolation methods ===

    /** Revoke all user sessions (for account deletion) */
    async revokeAllUserSessions(userId: string): Promise<number> {
      return db(REFRESH_TOKENS).where({ user_id: userId }).delete()
    },

    /** Anonymize user audit records (GDPR Art. 17 - keep for stats, remove PII) */
    async anonymizeUserAudit(userId: string): Promise<number> {
      return db('audit_log')
        .where({ actor_id: userId })
        .update({
          actor_id: null,
          actor_email: null,
          ip_address: '[ANONYMIZED]',
          user_agent: '[ANONYMIZED]'
        })
    },

    /** Get user auth data for GDPR export (Art. 15) */
    async getUserAuthData(userId: string): Promise<{ sessions: unknown[]; auditLog: unknown[] }> {
      const sessions = await db(REFRESH_TOKENS)
        .select('id', 'device_name', 'device_id', 'last_used_at', 'created_at', 'expires_at')
        .where({ user_id: userId })
        .orderBy('created_at', 'desc')

      const auditLog = await auditService.query({
        actorId: userId,
        source: 'core:auth',
        limit: 100
      })

      return { sessions, auditLog }
    },

    // === Identity Management (for auth plugins) ===

    /** Find an external identity by provider and provider user ID */
    async findIdentity(provider: string, providerUserId: string): Promise<AuthIdentity | undefined> {
      return db(AUTH_IDENTITIES)
        .where({ provider, provider_user_id: providerUserId })
        .first()
    },

    /** Find all identities for a user, optionally filtered by provider */
    async findIdentitiesByUser(userId: string, provider?: string): Promise<AuthIdentity[]> {
      const query = db(AUTH_IDENTITIES).where({ user_id: userId })
      if (provider) query.where({ provider })
      return query
    },

    /** Link an external identity to a Nexus user */
    async linkIdentity(input: LinkIdentityInput): Promise<AuthIdentity> {
      const now = nowTimestamp(db)
      const record = {
        id: generateId(),
        user_id: input.userId,
        provider: input.provider,
        provider_user_id: input.providerUserId,
        provider_email: input.providerEmail ?? null,
        metadata: input.metadata ? JSON.stringify(input.metadata) : null,
        linked_at: now,
        last_login_at: now,
      }
      try {
        await db(AUTH_IDENTITIES).insert(record)
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e)
        if (msg.includes('UNIQUE') || msg.includes('unique') || msg.includes('duplicate')) {
          throw new errors.ConflictError(`Identity already linked: ${input.provider}/${input.providerUserId}`)
        }
        throw e
      }
      return record as unknown as AuthIdentity
    },

    /** Unlink an external identity */
    async unlinkIdentity(provider: string, providerUserId: string): Promise<boolean> {
      const deleted = await db(AUTH_IDENTITIES)
        .where({ provider, provider_user_id: providerUserId })
        .delete()
      return deleted > 0
    },

    /** Update last login timestamp for an identity */
    async updateIdentityLogin(provider: string, providerUserId: string): Promise<void> {
      await db(AUTH_IDENTITIES)
        .where({ provider, provider_user_id: providerUserId })
        .update({ last_login_at: nowTimestamp(db) })
    },

    // === Personal Access Tokens ===

    async createPersonalToken(userId: string, input: CreatePersonalTokenInput, requestInfo?: RequestInfo) {
      const rawToken = 'nxs_' + randomBytes(32).toString('hex')
      const tokenHash = createHash('sha256').update(rawToken).digest('hex')
      const tokenPrefix = 'nxs_...' + rawToken.slice(-6)

      const id = generateId()
      const now = nowTimestamp(db)

      await db(PERSONAL_TOKENS).insert({
        id,
        user_id: userId,
        name: input.name,
        token_prefix: tokenPrefix,
        token_hash: tokenHash,
        scope: input.scope,
        expires_at: input.expires_at ?? null,
        last_used_at: null,
        created_at: now,
        updated_at: now
      })

      auditService.log({ source: 'core:auth', action: 'pat_created', actorId: userId, ip: requestInfo?.ip, userAgent: requestInfo?.userAgent, metadata: { tokenName: input.name, scope: input.scope } })

      return {
        id,
        token: rawToken,
        name: input.name,
        scope: input.scope,
        expires_at: input.expires_at ?? null,
        created_at: now
      }
    },

    async listPersonalTokens(userId: string) {
      return db(PERSONAL_TOKENS)
        .where({ user_id: userId })
        .select('id', 'name', 'token_prefix', 'scope', 'expires_at', 'last_used_at', 'created_at')
        .orderBy('created_at', 'desc')
    },

    async revokePersonalToken(userId: string, tokenId: string, requestInfo?: RequestInfo): Promise<boolean> {
      const deleted = await db(PERSONAL_TOKENS)
        .where({ id: tokenId, user_id: userId })
        .delete()

      if (deleted > 0) {
        auditService.log({ source: 'core:auth', action: 'pat_revoked', actorId: userId, ip: requestInfo?.ip, userAgent: requestInfo?.userAgent, metadata: { tokenId } })
      }

      return deleted > 0
    },

    async validatePersonalToken(rawToken: string): Promise<{ userId: string; scope: PersonalTokenScope; tokenId: string } | null> {
      const tokenHash = createHash('sha256').update(rawToken).digest('hex')

      // Check cache first (key: hash, value: validation result, TTL: 60s)
      const cacheKey = `pat:${tokenHash}`
      const cached = await patValidationCache.get(cacheKey)
      if (cached) {
        return cached
      }

      const token = await db<PersonalToken>(PERSONAL_TOKENS)
        .where({ token_hash: tokenHash })
        .first()

      if (!token) return null

      // Check expiration
      if (token.expires_at && new Date(token.expires_at) < new Date()) {
        return null
      }

      const result = { userId: token.user_id, scope: token.scope, tokenId: token.id }

      // Cache result (60s TTL)
      await patValidationCache.set(cacheKey, result, 60)

      // Update last_used_at (fire-and-forget, throttled: only if not recently updated)
      const usageKey = `pat_usage:${token.id}`
      if (!await authCache.get(usageKey)) {
        await authCache.set(usageKey, 1 as any, 60) // Throttle: max once per 60s
        db(PERSONAL_TOKENS)
          .where({ id: token.id })
          .update({ last_used_at: nowTimestamp(db) })
          .catch(() => {}) // Fire-and-forget
      }

      return result
    },

    /** Revoke all personal tokens for a user (for account deletion) */
    async revokeAllPersonalTokens(userId: string): Promise<number> {
      return db(PERSONAL_TOKENS).where({ user_id: userId }).delete()
    },

    // === Auth Plugin Helpers ===

    /** Find a user by email (for auth plugins to check if user exists) */
    async findUserByEmail(email: string): Promise<{ id: string; email: string; name?: string } | null> {
      const user = await db<AuthUserRecord>(USERS).where({ email }).first()
      if (!user) return null
      return { id: user.id, email: user.email!, name: user.name ?? undefined }
    },

    /** Find a user by ID (for auth plugins) */
    async findUserById(id: string): Promise<{ id: string; email: string; name?: string } | null> {
      const user = await db<AuthUserRecord>(USERS).where({ id }).first()
      if (!user) return null
      return { id: user.id, email: user.email!, name: user.name ?? undefined }
    },

    /** Create a new user without password (for auth plugins using external providers) */
    async createUser(data: { email: string; name?: string; role?: string }): Promise<{ id: string; email: string; name?: string }> {
      if (authConfig.disableAutoCreate) {
        throw new errors.ForbiddenError('AUTH_AUTO_CREATE_DISABLED', 'Auto-creation of users is disabled')
      }

      const user = await usersService.create({
        email: data.email,
        password: null, // OIDC users authenticate via external provider, no password needed
        name: data.name,
        type: 'human'
      })

      // Assign role if specified
      if (data.role) {
        const role = await rolesService.findByName(data.role)
        if (role) {
          await usersService.assignRole(user.id, role.id)
        }
      } else {
        // Assign default VIEWER role
        const viewerRole = await rolesService.findByName('VIEWER')
        if (viewerRole) {
          await usersService.assignRole(user.id, viewerRole.id)
        }
      }

      auditService.log({ source: 'core:auth', action: 'register', actorId: user.id, actorEmail: data.email, metadata: { source: 'oauth_plugin', role: data.role || 'VIEWER' } })

      return { id: user.id, email: user.email!, name: user.name ?? undefined }
    },

    /** Create session tokens for a user (for auth plugins after external authentication) */
    async createTokens(user: { id: string }): Promise<{ accessToken: string; refreshToken: string; expiresIn: number }> {
      const fullUser = await db<AuthUserRecord>(USERS).where({ id: user.id }).first()
      if (!fullUser) throw new errors.NotFoundError('User')

      const roleIds = await usersService.getRoleIds(user.id)
      const roleNames = await usersService.getRoleNames(user.id)

      const tokens = await safeGenerateTokenPair({
        userId: fullUser.id,
        email: fullUser.email!,
        roleIds,
        roleNames
      }, fullUser as Record<string, unknown>)

      await db(REFRESH_TOKENS).insert({
        id: generateId(),
        token: tokens.refreshToken,
        user_id: fullUser.id,
        expires_at: getRefreshTokenExpiration(authConfig)
      })

      // Parse expiresIn from config (e.g., '15m' → 900)
      const expMatch = authConfig.accessExpires.match(/^(\d+)([smhd])$/)
      let expiresIn = 900
      if (expMatch) {
        const val = parseInt(expMatch[1]!, 10)
        const unit = expMatch[2]
        expiresIn = val * (unit === 's' ? 1 : unit === 'm' ? 60 : unit === 'h' ? 3600 : 86400)
      }

      auditService.log({ source: 'core:auth', action: 'oauth_login', actorId: fullUser.id, actorEmail: fullUser.email! })

      return {
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        expiresIn
      }
    },

    async updateUser(id: string, data: { email?: string; name?: string; is_active?: boolean }): Promise<void> {
      const updates: Record<string, unknown> = {}
      if (data.email !== undefined) updates['email'] = data.email
      if (data.name !== undefined) updates['name'] = data.name
      if (data.is_active !== undefined) updates['is_active'] = data.is_active
      if (Object.keys(updates).length > 0) {
        await db(USERS).where({ id }).update(updates)
      }
    },

    async deleteUser(id: string): Promise<void> {
      await db(USERS).where({ id }).delete()
    }
  }
}
